import { useEffect } from "react"
import { Controller, FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { Alert, Checkbox, Divider, FormControlLabel, Grid, Switch, Typography } from "@mui/material"

import { BodyPage, FormContainerPage } from "../../components/container/container.styles"
import { DialogHistoric } from "../../components/dialog-historic/dialog-historic"
import DialogAlert from "../../components/dialog/dialog"
import { HeaderPage } from "../../components/header/page/header-page"
import { Loading } from "../../components/loading/loading"
import { ProductSelect } from "../../components/product-select/product-select"
import { SnackBar } from "../../components/snackbar/snackbar"
import { ControlledTextField } from "../../components/text-field/controlled-text-field"
import { useAPIKey } from "../../hooks/api-key/use-api-key"
import { pathNames } from "../../routes/paths"
import { styles } from "../../styles/common"

export const EditAPIKey = () => {
	const { t } = useTranslation()
	const { apiKeyId } = useParams()

	const {
		methods,
		fetchApiKey,
		loading,
		dialog,
		dialogApiKey,
		dialogTitle,
		snackbar,
		dialogDescription,
		handleResetApiKey,
		setDialogApiKey,
		setSnackbar,
		snackbarType,
		snackbarMessage,
		showDialogHistoric,
		setShowDialogHistoric,
		setShowDeleteDialog,
		showDeleteDialog,
		products,
		setActive,
		handleUpdateOrganization,
		navigate,
		handleAccept,
		handleAddProduct,
		handleDeleteProduct,
		handleUpdateValue,
	} = useAPIKey()

	const {
		control,
		getValues,
		formState: { errors },
	} = methods

	useEffect(() => {
		fetchApiKey(apiKeyId as string)
		// eslint-disable-next-line
	}, [])

	if (loading) return <Loading />

	return (
		<FormContainerPage onSubmit={handleUpdateOrganization}>
			<HeaderPage
				onClickTopButton={() => navigate(pathNames.apiKey.list)}
				description={t("user.edit_api_key_description")}
				isEdit={true}
				title={t("user.edit_api_key_title")}
			/>

			<BodyPage>
				<FormProvider {...methods}>
					<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
						<Grid item xs={10}>
							<ControlledTextField
								id={"key"}
								style={styles.textFieldMarginTop}
								label={t("columns.apiKey")}
								control={control}
								rules={{ required: true }}
								readOnly={true}
								endIcon={<RestartAltIcon />}
								handleClickEndIcon={() => setDialogApiKey(true)}
							/>
						</Grid>

						<Grid style={styles.active} item xs={2}>
							<Grid style={styles.active} item xs={2}>
								<Controller
									control={control}
									name="active"
									render={({ field: { value, ...fieldValues } }) => (
										<FormControlLabel
											control={<Switch {...fieldValues} color={"secondary"} checked={value} />}
											label={
												<Typography
													color={"secondary"}
												>{`${value ? "Ativo" : "Inativo"}`}</Typography>
											}
										/>
									)}
								/>
							</Grid>
						</Grid>

						<Grid item xs={10}>
							<ControlledTextField
								id={"description"}
								style={styles.fullWidth}
								label={t("columns.description")}
								control={control}
								rules={{ required: false }}
								errors={errors.description}
								helperText={errors.description?.message}
							/>
						</Grid>

						<Grid item xs={2}>
							<Controller
								control={control}
								name="trial"
								render={({ field: { value, ...fieldValues } }) => (
									<FormControlLabel
										control={<Checkbox {...fieldValues} disabled checked={Boolean(value)} />}
										label={t("columns.trial")}
									/>
								)}
							/>
						</Grid>
					</Grid>

					{getValues("trial") && (
						<>
							<Divider style={styles.divider} />

							<Grid margin={"0 0 8px 0"}>
								<Typography color={"secondary"} variant={"h6"}>
									{t("api_keys.lead_data_title")}
								</Typography>

								<Typography color="textPrimary" variant={"body2"}>
									{t("api_keys.lead_data_description")}
								</Typography>
							</Grid>

							<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
								<Grid item xs={6}>
									<ControlledTextField
										id={"lead.name"}
										style={styles.fullWidth}
										label={t("columns.name")}
										control={control}
										rules={{ required: true }}
										errors={errors.lead?.name}
										helperText={errors.lead?.name?.message}
									/>
								</Grid>

								<Grid item xs={3}>
									<ControlledTextField
										id={"lead.company"}
										style={styles.fullWidth}
										label={t("columns.company")}
										control={control}
										rules={{ required: false }}
										errors={errors.lead?.company}
										helperText={errors.lead?.company?.message}
									/>
								</Grid>

								<Grid item xs={3}>
									<ControlledTextField
										id={"lead.role"}
										style={styles.fullWidth}
										label={t("columns.role")}
										control={control}
										rules={{ required: false }}
										errors={errors.lead?.role}
										helperText={errors.lead?.role?.message}
									/>
								</Grid>

								<Grid item xs={4}>
									<ControlledTextField
										id={"lead.utm"}
										style={styles.fullWidth}
										label={t("columns.utm")}
										control={control}
										rules={{ required: false }}
										errors={errors.lead?.utm}
										helperText={errors.lead?.utm?.message}
									/>
								</Grid>

								<Grid item xs={4}>
									<ControlledTextField
										id={"lead.email"}
										label={t("organizations.label_billing_email")}
										control={control}
										type={"email"}
										errors={errors.lead?.email}
										// eslint-disable-next-line
										validChars={
											/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
										}
										style={styles.fullWidth}
										helperText={errors.lead?.email?.message}
									/>
								</Grid>

								<Grid item xs={4}>
									<ControlledTextField
										id={"lead.phone"}
										style={styles.fullWidth}
										label={t("customer.label_phone")}
										control={control}
										rules={{ required: true }}
										mask={"(99) 99999-9999"}
										errors={errors.lead?.phone}
										helperText={errors.lead?.phone?.message}
									/>
								</Grid>

								<Grid item xs={12}>
									<ControlledTextField
										id={"lead.observation"}
										style={styles.fullWidth}
										label={t("columns.observation")}
										control={control}
										rules={{ required: false }}
										errors={errors.lead?.observation}
										helperText={errors.lead?.observation?.message}
									/>
								</Grid>
							</Grid>
						</>
					)}

					<Divider style={styles.divider} />

					<ProductSelect
						handleUpdateValue={handleUpdateValue}
						onClickHistoric={() => setShowDialogHistoric(true)}
						isHistoricVisible={true}
						dataProducts={products}
						handleAddProduct={handleAddProduct}
						handleDeleteProduct={handleDeleteProduct}
					/>
				</FormProvider>
			</BodyPage>
			<DialogAlert
				title={t("api_keys.disable_message_title")}
				description={t("api_keys.disable_message_description")}
				show={showDeleteDialog}
				accept={() => {
					setActive(false)
					setShowDeleteDialog(false)
				}}
				decline={() => {
					setShowDeleteDialog(false)
					setActive(true)
				}}
				props={
					<Alert style={{ marginTop: 16 }} severity={"info"}>
						{t("api_keys.disable_info")}
					</Alert>
				}
			/>

			<DialogAlert
				show={dialogApiKey}
				title={t("profile.dialog_reset_title")}
				description={t("profile.dialog_reset_description")}
				accept={handleResetApiKey}
				decline={() => setDialogApiKey(false)}
			/>
			<SnackBar
				open={snackbar}
				handleClose={() => setSnackbar(false)}
				type={snackbarType}
				message={snackbarMessage}
			/>

			<DialogAlert
				onlyConfirm={true}
				show={dialog}
				accept={handleAccept}
				title={dialogTitle}
				description={dialogDescription}
			/>

			<DialogHistoric
				id={apiKeyId}
				handleClose={() => setShowDialogHistoric(false)}
				open={showDialogHistoric}
			/>
		</FormContainerPage>
	)
}
